var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { createContext, useState, useMemo } from 'react';
import { uuid } from '@gopeerproject/chuck';
import { IMAGE_TYPES, SUPPORTED_FILE_TYPES } from '../../../constants';
import { FileStatus } from '../types';
import { Platform } from 'react-native';
const Context = createContext(undefined);
const getPreview = (data) => __awaiter(void 0, void 0, void 0, function* () {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            if (e.target)
                resolve(e.target.result);
            else
                reject();
        };
        reader.readAsDataURL(data);
    });
});
export const FileInputContextProvider = (_a) => {
    var { children, getCanceler, upload } = _a, props = __rest(_a, ["children", "getCanceler", "upload"]);
    const [files, setFiles] = useState([]);
    const [showUnsupportedFilesWarning, setShowUnsupportedFilesWarning] = useState(false);
    const [showOversizeFilesWarning, setShowOversizeFilesWarning] = useState(false);
    const [invalidFiles, setInvalidFiles] = useState([]);
    const add = (data) => __awaiter(void 0, void 0, void 0, function* () {
        const unsupportedItems = data.filter((file) => !SUPPORTED_FILE_TYPES.includes(file.type));
        const oversizeItems = data.filter((file) => file.size / 1024 / 1024 > 25);
        const supportedItems = data.filter((file) => !unsupportedItems.includes(file) && !oversizeItems.includes(file));
        const items = yield Promise.all(supportedItems.map((file) => __awaiter(void 0, void 0, void 0, function* () {
            return ({
                id: uuid(),
                status: FileStatus.Uploading,
                progress: 0,
                loaded: 0,
                total: Number.MAX_VALUE,
                data: file,
                preview: IMAGE_TYPES.includes(file.type) && Platform.OS === 'web'
                    ? yield getPreview(file)
                    : null,
                type: file.type,
                name: file.name,
                canceler: getCanceler(),
                localId: file.localId,
                uri: file.uri
            });
        })));
        setFiles((files) => [...files, ...items]);
        items.forEach((file) => {
            upload(file, setFiles);
        });
        if (unsupportedItems.length) {
            setInvalidFiles(unsupportedItems);
            setShowUnsupportedFilesWarning(true);
        }
        else if (oversizeItems.length) {
            setInvalidFiles(oversizeItems);
            setShowOversizeFilesWarning(true);
        }
    });
    const remove = (id) => {
        var _a;
        const file = files.find((file) => id === file.id || id === file.localId);
        const prev = files;
        if (!file)
            return;
        if (file.status === FileStatus.Uploading) {
            (_a = file.canceler) === null || _a === void 0 ? void 0 : _a.cancel();
        }
        else
            props.remove(id, {
                onError: () => {
                    setFiles(prev);
                }
            });
        setFiles((files) => files.filter((file) => file.id !== id && file.localId !== id));
    };
    const uploadProgress = useMemo(() => {
        if (files.reduce((acc, v) => acc && v.progress === 0, true))
            return 0;
        const loaded = files.reduce((acc, v) => acc + v.loaded, 0);
        const total = files.reduce((acc, v) => acc + v.total, 0);
        return total ? Math.round((loaded * 100) / total) : 100;
    }, [files]);
    const processing = useMemo(() => {
        if (uploadProgress !== 100)
            return true;
        return files.reduce((acc, v) => acc || v.status !== 'uploaded', false);
    }, [uploadProgress, files]);
    return (React.createElement(Context.Provider, { value: {
            files,
            setFiles,
            showUnsupportedFilesWarning,
            setShowUnsupportedFilesWarning,
            showOversizeFilesWarning,
            setShowOversizeFilesWarning,
            invalidFiles,
            setInvalidFiles,
            add,
            remove,
            uploadProgress,
            processing
        } }, children));
};
export { Context as FileInputContext };
//# sourceMappingURL=FileInputContext.js.map